import React, { useMemo, useState, useEffect } from "react";
import { ProcessingStatus, UploadStatus } from "..";
import { Loader, CheckCircle, AlertCircle, FileText } from "lucide-react";
import Link from "next/link";
type ProcessingStatusDisplayProps = {
  processingStatus: ProcessingStatus | null;
  uploadStatus: UploadStatus;
};

// 処理状態の型を定義
type ProcessState = 'reqUpload' | 'errUpload' | 'req2text' | 'err2text' | 
                   'reqStructured' | 'errStructured' | 'reqCompleted' | 'unknown';

// 進捗状態の配列を定義 - 正常な処理フローの状態のみ
const PROCESS_FLOW = ["reqUpload", "req2text", "reqStructured", "reqCompleted"];

export const ProcessingStatusDisplay = ({ processingStatus, uploadStatus }: ProcessingStatusDisplayProps) => {
  // 動的な進捗率を管理するstate
  const [dynamicPercentage, setDynamicPercentage] = useState(0);

  // 現在の処理状態を取得
  const currentState = useMemo<ProcessState>(() => {
    return processingStatus?.states as ProcessState || 'unknown';
  }, [processingStatus]);

  // 状態に応じた進捗率の範囲を定義
  const getProgressRange = (state: ProcessState) => {
    switch (state) {
      case "reqUpload":
        return { min: 0, max: 32 };
      case "req2text":
        return { min: 33, max: 65 };
      case "reqStructured":
        return { min: 66, max: 99 };
      case "reqCompleted":
        return { min: 100, max: 100 };
      default:
        return { min: 0, max: 0 };
    }
  };

  // 状態が変更されたときに進捗率を適切な開始位置に設定
  useEffect(() => {
    const range = getProgressRange(currentState);
    if (currentState === "reqCompleted") {
      setDynamicPercentage(100);
    } else if (currentState !== "unknown" && !currentState.startsWith("err")) {
      setDynamicPercentage(range.min);
    } else {
      setDynamicPercentage(0);
    }
  }, [currentState]);

  // 0.75秒ごとに1%ずつ進捗率を上昇させる
  useEffect(() => {
    if (currentState === "unknown" || currentState.startsWith("err") || currentState === "reqCompleted") {
      return;
    }

    const range = getProgressRange(currentState);
    const interval = setInterval(() => {
      setDynamicPercentage(prev => {
        if (prev >= range.max) {
          return range.max;
        }
        return prev + 1;
      });
    }, 750); // 0.75秒ごとに実行

    return () => clearInterval(interval);
  }, [currentState]);

  // 進捗表示の計算
  const progressInfo = useMemo(() => {
    // エラー状態や不明な状態の場合は0%を返す
    const isError = currentState.startsWith("err");
    const isValidState = PROCESS_FLOW.indexOf(currentState) !== -1 && !isError;
    
    // 動的な進捗率を使用
    const percentage = isValidState ? dynamicPercentage : 0;

    return {
      percentage,
      isCompleted: currentState === "reqCompleted",
      isError,
      isProcessing: isValidState && !isError && currentState !== "reqCompleted"
    };
  }, [currentState, dynamicPercentage]);

  // 処理状態メッセージを取得
  const statusMessage = useMemo(() => {
    if (!processingStatus) return uploadStatus.message;
    
    if (progressInfo.isCompleted) {
      return "処理完了！レポートが生成されました。";
    } else if (progressInfo.isError) {
      return `エラーが発生しました: ${currentState}`;
    } else {
      // 処理状態に応じたメッセージを返す
      switch (currentState) {
        case "reqUpload":
          return "音声ファイルをアップロードしています...";
        case "req2text":
          return "音声を文字に変換しています...";
        case "reqStructured":
          return "レポートを生成しています...";
        default:
          return `処理中... ${progressInfo.percentage}%`;
      }
    }
  }, [processingStatus, uploadStatus.message, currentState, progressInfo]);

  // ユーザーガイダンスメッセージを取得
  const guidanceMessage = useMemo(() => {
    if (!processingStatus) return null;
    
    if (progressInfo.isCompleted) {
      return "下部に表示されているレポートの内容を確認してください。必要に応じて編集や修正が可能です。";
    } else if (progressInfo.isError) {
      return "エラーが発生しました。もう一度音声ファイルをアップロードしてください。";
    } else {
      switch (currentState) {
        case "reqUpload":
          return "アップロードが完了するまでお待ちください。大きなファイルの場合は時間がかかる場合があります。";
        case "req2text":
          return "音声の文字起こしを行っています。音声の長さによって処理時間が変わります。";
        case "reqStructured":
          return "AIがレポートを生成しています。もうしばらくお待ちください。";
        default:
          return "処理を実行中です。しばらくお待ちください。";
      }
    }
  }, [processingStatus, currentState, progressInfo]);

  // 処理中のタブ閉じに関する追加の注意書きを取得
  const processingNote = useMemo(() => {
    // 処理中の場合のみ表示（完了またはエラーの場合は表示しない）
    if (processingStatus && progressInfo.isProcessing) {
      return (
        <div className="mt-4 p-3 rounded-md text-sm text-gray-600 dark:text-gray-300 border border-gray-200/30 dark:border-gray-700/30 bg-blue-50/30 dark:bg-blue-900/20">
          <p>
            このタブを閉じても処理は継続されます。<br />
            処理が終わり次第、<Link href={`${process.env.NEXT_BASE_URL}reports`} className="text-blue-600 dark:text-blue-400 hover:underline">このページ</Link>から確認できます。
          </p>
        </div>
      );
    }
    return null;
  }, [processingStatus, progressInfo.isProcessing]);

  // 状態に応じたスタイルを取得
  const getStatusIcon = () => {
    if (processingStatus) {
      if (progressInfo.isCompleted) {
        return <CheckCircle className="h-6 w-6 text-success" />;
      } else if (progressInfo.isError) {
        return <AlertCircle className="h-6 w-6 text-error" />;
      } else if (progressInfo.isProcessing) {
        return <Loader className="h-6 w-6 animate-spin text-primary" />;
      }
    }
    
    switch (uploadStatus.state) {
      case "SUCCESS":
        return <FileText className="h-6 w-6 text-primary" />;
      case "ERROR":
        return <AlertCircle className="h-6 w-6 text-error" />;
      case "SAVING":
        return <Loader className="h-6 w-6 animate-spin text-primary" />;
      default:
        return null;
    }
  };

  // 表示するかどうかの判定
  if (!processingStatus && uploadStatus.state === "PENDING") {
    return null;
  }

  return (
    <div className="mt-4">
      <div className="fade-in slide-up p-6 max-w-lg min-w-96 mx-auto transition-component transition-component-enter-active" style={{
        // backgroundColor: 'rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(7px)',
        WebkitBackdropFilter: 'blur(7px)',
        borderRadius: 'var(--card-radius)',
        border: 'var(--glass-border)',
        boxShadow: 'var(--glass-shadow)'
      }}>
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0 mt-1">
            {getStatusIcon()}
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-lg mb-2 text-foreground">
              {processingStatus && progressInfo.isCompleted 
                ? "レポート生成完了" 
                : processingStatus && progressInfo.isError
                  ? "エラーが発生しました"
                  : "処理状況"}
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
              {statusMessage}
            </p>
            
            {processingStatus && !progressInfo.isError && (
              <div className="mb-4">
                <div className="h-2 bg-gray-200/30 dark:bg-gray-700/30 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${progressInfo.isCompleted ? 'bg-success' : 'bg-black dark:bg-white'} rounded-full transition-all duration-300 ease-out`}
                    style={{ width: `${progressInfo.percentage}%` }}
                  ></div>
                </div>
                <div className="flex justify-between mt-1 text-xs text-gray-500 dark:text-gray-400">
                  <span>進捗状況</span>
                  {/* Progressバー */}
                  <span className="transition-all duration-300">{progressInfo.percentage}%</span>
                </div>
              </div>
            )}

            {guidanceMessage && (
              <div className="p-4 rounded-md text-sm text-gray-600 dark:text-gray-300 border border-gray-200/30 dark:border-gray-700/30 transition-all duration-300 fade-scale-in" style={{ 
                backgroundColor: 'rgba(248, 249, 250, 0.2)', 
                backdropFilter: 'blur(5px)', 
                WebkitBackdropFilter: 'blur(5px)'
              }}>
                <p>{guidanceMessage}</p>
              </div>
            )}
            
            {/* 処理中の場合に表示する追加のメッセージ */}
            {processingNote}
          </div>
        </div>
      </div>
    </div>
  );
};